import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, Zap, Eye, MessageCircle, Calendar, Trash2, CheckCheck, ArrowLeft } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sampleImages = [
  'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=200',
  'https://images.unsplash.com/photo-1762708546140-7aea0b34ecac?crop=entropy&cs=srgb&fm=jpg&q=85&w=200',
  'https://images.unsplash.com/photo-1582439170934-d089aa10abda?crop=entropy&cs=srgb&fm=jpg&q=85&w=200',
  'https://images.unsplash.com/photo-1635631191694-8fc637d53429?crop=entropy&cs=srgb&fm=jpg&q=85&w=200'
];

const Notifications = () => {
  const navigate = useNavigate();
  const { notifications, markNotificationRead, clearNotifications } = useNotifications();
  const [activeTab, setActiveTab] = useState('all');
  const [taps, setTaps] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotificationData();
    // Mark activity as seen when visiting
    axios.post(`${API}/activity/mark-seen`).catch(() => {});
  }, []);

  const fetchNotificationData = async () => {
    setLoading(true);
    try {
      const [tapsRes, viewersRes] = await Promise.all([
        axios.get(`${API}/users/taps/received`),
        axios.get(`${API}/users/viewers`)
      ]);
      setTaps(tapsRes.data || []);
      setViewers(viewersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch notification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (user, index = 0) => {
    if (user?.public_photos && user.public_photos.length > 0) {
      const photo = user.public_photos[0];
      if (photo.startsWith('data:') || photo.startsWith('http')) {
        return photo;
      }
      return `data:image/jpeg;base64,${photo}`;
    }
    return sampleImages[index % sampleImages.length];
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const NotificationItem = ({ icon: Icon, iconColor, title, subtitle, time, onClick, isUnread }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 w-full text-left hover:bg-white/5 transition-colors border-b border-white/5 ${
        isUnread ? 'bg-[#FF2E63]/5' : ''
      }`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconColor}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isUnread ? 'font-semibold text-white' : 'text-[#EDEDED]'}`}>
          {title}
        </p>
        {subtitle && (
          <p className="text-xs text-[#A1A1AA] truncate">{subtitle}</p>
        )}
      </div>
      {time && (
        <span className="text-xs text-[#52525B] flex-shrink-0">{time}</span>
      )}
      {isUnread && (
        <div className="w-2 h-2 bg-[#FF2E63] rounded-full flex-shrink-0" />
      )}
    </button>
  );

  const UserNotificationItem = ({ user, type, time, index }) => (
    <button
      onClick={() => navigate(`/profile/${user.id}`)}
      className="flex items-center gap-3 p-4 w-full text-left hover:bg-white/5 transition-colors border-b border-white/5"
    >
      <div className="relative flex-shrink-0">
        <img
          src={getPhotoUrl(user, index)}
          alt={user.display_name || user.username}
          className="w-12 h-12 rounded-full object-cover"
        />
        {user.is_online && (
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#08D9D6] rounded-full border-2 border-[#050505]" />
        )}
        <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
          type === 'tap' ? 'bg-[#FF2E63]' : type === 'match' ? 'bg-[#22c55e]' : 'bg-[#08D9D6]'
        }`}>
          {type === 'tap' || type === 'match' ? (
            <Zap className="w-3 h-3 text-white" />
          ) : (
            <Eye className="w-3 h-3 text-white" />
          )}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {user.display_name || user.username}
          {user.age && <span className="font-normal text-[#A1A1AA]">, {user.age}</span>}
        </p>
        <p className="text-xs text-[#A1A1AA]">
          {type === 'match' ? 'Matched with you!' : type === 'tap' ? 'Tapped you' : 'Viewed your profile'}
        </p>
      </div>
      <span className="text-xs text-[#52525B] flex-shrink-0">{time}</span>
    </button>
  );

  const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Icon className="w-12 h-12 text-[#52525B] mb-4" />
      <h3 className="text-lg font-semibold text-[#EDEDED] mb-2">{title}</h3>
      <p className="text-sm text-[#A1A1AA]">{description}</p>
    </div>
  );

  // Combine and sort all notifications by time
  const allNotifications = [
    ...taps.map((user, i) => ({ 
      type: user.is_mutual ? 'match' : 'tap', 
      user, 
      time: user.tapped_at || new Date().toISOString(),
      index: i 
    })),
    ...viewers.map((user, i) => ({ 
      type: 'view', 
      user, 
      time: user.viewed_at || new Date().toISOString(),
      index: i 
    }))
  ].sort((a, b) => new Date(b.time) - new Date(a.time));

  const filteredNotifications = activeTab === 'all' 
    ? allNotifications 
    : activeTab === 'taps' 
      ? allNotifications.filter(n => n.type === 'tap' || n.type === 'match')
      : allNotifications.filter(n => n.type === 'view');

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/grid')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-[#FF2E63]">Notifications</h1>
          </div>
          {allNotifications.length > 0 && (
            <Button
              data-testid="clear-all-btn"
              variant="ghost"
              size="sm"
              onClick={clearNotifications}
              className="text-[#A1A1AA] hover:text-white text-xs"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="px-4 pt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-white/5 mb-4">
            <TabsTrigger 
              data-testid="all-tab"
              value="all" 
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white flex items-center gap-2"
            >
              <Bell className="w-4 h-4" />
              All
            </TabsTrigger>
            <TabsTrigger 
              data-testid="taps-tab"
              value="taps"
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Taps
            </TabsTrigger>
            <TabsTrigger 
              data-testid="views-tab"
              value="views"
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Views
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <EmptyState 
              icon={Bell}
              title="No notifications yet"
              description="When someone taps you or views your profile, you'll see it here"
            />
          ) : (
            <div className="glass rounded-xl overflow-hidden">
              {filteredNotifications.map((notification, i) => (
                <UserNotificationItem
                  key={`${notification.type}-${notification.user.id}-${i}`}
                  user={notification.user}
                  type={notification.type}
                  time={formatTime(notification.time)}
                  index={notification.index}
                />
              ))}
            </div>
          )}
        </Tabs>
      </div>

      {/* In-app notifications from context */}
      {notifications.length > 0 && (
        <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#A1A1AA]">Recent Activity</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearNotifications}
              className="text-[#52525B] hover:text-white text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </div>
          <div className="glass rounded-xl overflow-hidden">
            {notifications.slice(0, 5).map((notif) => (
              <NotificationItem
                key={notif.id}
                icon={notif.type === 'match' ? Zap : notif.type === 'tap' ? Zap : Eye}
                iconColor={notif.type === 'match' ? 'bg-[#22c55e]' : 'bg-[#FF2E63]'}
                title={notif.message}
                time={formatTime(notif.timestamp)}
                onClick={() => {
                  markNotificationRead(notif.id);
                  if (notif.user?.id) {
                    navigate(`/profile/${notif.user.id}`);
                  }
                }}
                isUnread={!notif.read}
              />
            ))}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Notifications;
